import http, { handleHttpError, handleHttpResponse } from '@/services/http'
import { HTTPResponse } from '@/services/http/http.types.ts'
import {
  IDashboard,
  IDashboardsWithPagination,
  IWidget,
  IDashboardGroup,
  TCreateDashboardGroupDTO,
  TCreateDashboardDTO,
} from './dashboards.types'

const createGroup = (dto: TCreateDashboardGroupDTO): Promise<HTTPResponse<IDashboardGroup>> => {
  return http
    .post('/nsi/v1/dashboards/groups', { name: dto.name, parentId: dto.parentId > 0 ? dto.parentId : null })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const updateGroup = (dto: Omit<IDashboardGroup, 'createdAt' | 'updatedAt'>): Promise<HTTPResponse<IDashboardGroup>> => {
  return http
    .patch(`/nsi/v1/dashboards/groups/${dto.id}`, { name: dto.name, parentId: dto.parentId > 0 ? dto.parentId : null })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const deleteGroup = (id: IDashboardGroup['id']): Promise<HTTPResponse<IDashboardGroup>> => {
  return http.delete(`/nsi/v1/dashboards/groups/${id}`).then(handleHttpResponse).catch(handleHttpError)
}

const loadDashboards = (): Promise<HTTPResponse<IDashboardsWithPagination>> => {
  return http.get(`/nsi/v1/dashboards?limit=${1000}`).then(handleHttpResponse).catch(handleHttpError)
}

const getDashboard = async (id: IDashboard['id']): Promise<HTTPResponse<IDashboard>> => {
  return http
    .get('/nsi/v1/dashboards/' + id)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const createDashboard = (dto: TCreateDashboardDTO): Promise<HTTPResponse<IDashboard>> => {
  return http
    .post(`/nsi/v1/dashboards`, { name: dto.name, groupId: dto.groupId })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const updateDashboard = (dto: TCreateDashboardDTO & { id: IDashboard['id'] }): Promise<HTTPResponse<IDashboard>> => {
  return http.patch(`/nsi/v1/dashboards/${dto.id}`, { name: dto.name }).then(handleHttpResponse).catch(handleHttpError)
}

const deleteDashboard = (id: IDashboard['id']): Promise<HTTPResponse<IDashboard>> => {
  return http.delete(`/nsi/v1/dashboards/${id}`).then(handleHttpResponse).catch(handleHttpError)
}

const importDashboards = (formData: FormData) => {
  return http.post('/nsi/v1/dashboards/import', formData).then(handleHttpResponse).catch(handleHttpError)
}

const createWidget = (widget: Omit<IWidget, 'id' | 'createdAt' | 'updatedAt'>): Promise<HTTPResponse<IWidget>> => {
  return http.post('/nsi/v1/dashboards/widgets', widget).then(handleHttpResponse).catch(handleHttpError)
}

const updateWidget = (id: IWidget['id'], updates: Partial<IWidget>): Promise<HTTPResponse<IWidget>> => {
  return http
    .patch('/nsi/v1/dashboards/widgets/' + id, updates)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const deleteWidget = (id: IWidget['id']): Promise<HTTPResponse<IWidget>> => {
  return http
    .delete('/nsi/v1/dashboards/widgets/' + id)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const dashboardsApi = {
  getDashboard,
  loadDashboards,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  importDashboards,
  createWidget,
  updateWidget,
  deleteWidget,
  createGroup,
  updateGroup,
  deleteGroup,
}

export default dashboardsApi
